import React, { useState, useEffect } from 'react';
import { Input, Button, message, InputNumber, Select, Radio, Upload, Spin, Tooltip, Table, Typography } from 'antd';
import { FileText, Upload as UploadIcon } from 'lucide-react';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';


const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

// Field mappings: API fields to formData keys
const fieldMapping = {
  "Advisor's Note (Optional)": "advisors_note",
  "Advisor Yearly Compensation": "advisor_yearly_compensation",
  "Plan Size": "plan_size",
  "Participation Rate": "participation_rate",
  "Number of Participants": "number_of_participants",
  "Deferral Rate": "deferral_rate",
  "Average Account Balance": "average_account_balance",
  "Loan Withdrawal Usage": "loan_withdrawal_usage",
  "Employees Registered Online": "employees_registered_online",
  "Employees Opting For Paper Statements": "employees_opting_for_paper_statements",
  "Interaction Rate": "interaction_rate",
  "Number of Plans Available": "number_of_plans_available",
  "Employees Build Their Own Portfolio": "employees_build_their_own_portfolio",
  "Use Target or Investment Models": "use_target_or_investment_models"
};

// Display names for a professional look
const displayNameMap = {
  company_name: "Company Name",
  advisors_note: "Advisor's Note (Optional)",
  advisor_yearly_compensation: "Advisor Yearly Compensation",
  plan_size: "Plan Size",
  participation_rate: "Participation Rate",
  number_of_participants: "Number of Participants",
  deferral_rate: "Deferral Rate",
  average_account_balance: "Average Account Balance",
  loan_withdrawal_usage: "Loan Withdrawal Usage",
  employees_registered_online: "Employees Registered Online",
  employees_opting_for_paper_statements: "Employees Opting For Paper Statements",
  interaction_rate: "Interaction Rate",
  number_of_plans_available: "Number of Plans Available",
  employees_build_their_own_portfolio: "Employees Build Their Own Portfolio",
  use_target_or_investment_models: "Use Target or Investment Models"
};

const numericFields = [
  'advisor_yearly_compensation',
  'plan_size',
  'participation_rate',
  'number_of_participants',
  'deferral_rate',
  'average_account_balance',
  'loan_withdrawal_usage',
  'employees_registered_online',
  'employees_opting_for_paper_statements',
  'interaction_rate',
  'number_of_plans_available',
  'employees_build_their_own_portfolio',
  'use_target_or_investment_models'
];

// Apply numeric fallback for any null/undefined or non-numeric values
const applyNumericFallback = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 0;
  }
  return Number(value);
};

const AutomaticDataEntryForPlanPerformance = ({ onAddRow, onDataSubmitted }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    advisors_note: '',
    advisor_yearly_compensation: '',
    plan_size: '',
    participation_rate: '',
    number_of_participants: '',
    deferral_rate: '',
    average_account_balance: '',
    loan_withdrawal_usage: '',
    employees_registered_online: '',
    employees_opting_for_paper_statements: '',
    interaction_rate: '',
    number_of_plans_available: '',
    employees_build_their_own_portfolio: '',
    use_target_or_investment_models: ''
  });

  const [inputMethod, setInputMethod] = useState('manual');
  const [fileList, setFileList] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [showReview, setShowReview] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [companyData, setCompanyData] = useState([]);

  // Fetch client names when component mounts
  useEffect(() => {
    const fetchClientNames = async () => {
      try {
        const userId = localStorage.getItem('userid');
       const year = dayjs().year();;
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/client_names/${year}`, {
          headers: {
            'user-id': userId
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const options = data.client_names.map(name => ({
          value: name,
          label: name
        }));
        setCompanyOptions(options);
      } catch (error) {
        console.error('Error fetching client names:', error);
        message.error('Failed to load client list');
      }
    };

    fetchClientNames();
  }, []);

  // Add this useEffect to fetch existing Plan Performance data
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const userId = localStorage.getItem('userid');
       const year = dayjs().year();;
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/spreadsheet/${userId}/Plan Performance/${year}`, {
          headers: { 'user-id': userId }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Fetched Plan Performance data:", data);
        setCompanyData(data.data || []);
      } catch (error) {
        console.error('Error fetching Plan Performance data:', error);
        message.error('Failed to load plan performance data');
      }
    };

    fetchCompanyData();
  }, []);

  // Add this useEffect to prefill formData when company_name or companyData changes
  useEffect(() => {
    if (formData.company_name && companyData.length > 0) {
      const selectedCompany = companyData.find(c => c["Company Name"] === formData.company_name);
      if (selectedCompany) {
        const updatedData = { ...formData };
        
        // Map the spreadsheet fields to form fields
        for (const [spreadsheetKey, formKey] of Object.entries(fieldMapping)) {
          if (selectedCompany.hasOwnProperty(spreadsheetKey) && selectedCompany[spreadsheetKey] !== null) {
            updatedData[formKey] = selectedCompany[spreadsheetKey];
          }
        }

        // Apply numeric fallbacks for all numeric fields
        numericFields.forEach(field => {
          updatedData[field] = applyNumericFallback(updatedData[field]);
        });

        setFormData(updatedData);
      }
    }
  }, [formData.company_name, companyData]);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    try {
      const userId = localStorage.getItem('userid');
     const year = dayjs().year();; 

      if (inputMethod === 'manual') {
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/plan-performance/${year}`, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
            'user-id': userId
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to submit plan performance data');
        }

        const data = await response.json();
        message.success(data.message || 'Plan performance data submitted successfully');

        // Clear form after successful submission
        setFormData({
          company_name: '',
          advisors_note: '',
          advisor_yearly_compensation: '',
          plan_size: '',
          participation_rate: '',
          number_of_participants: '',
          deferral_rate: '',
          average_account_balance: '',
          loan_withdrawal_usage: '',
          employees_registered_online: '',
          employees_opting_for_paper_statements: '',
          interaction_rate: '',
          number_of_plans_available: '',
          employees_build_their_own_portfolio: '',
          use_target_or_investment_models: ''
        });

        if (onDataSubmitted) {
          onDataSubmitted();
        }
      } else {
        // PDF logic
        if (fileList.length === 0 || !formData.company_name) {
          message.error('Please select both a company and a PDF file');
          return;
        }

        setIsProcessing(true);
        const formDataObj = new FormData();
        formDataObj.append('file', fileList[0].originFileObj);
        formDataObj.append('company_name', formData.company_name);

        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/plan-performance/${year}/process-pdf`, {
          method: 'POST',
          headers: { 'user-id': userId },
          body: formDataObj
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.extracted_data) {
          message.success('PDF processed successfully. Please review the extracted data.');
          setExtractedData(data.extracted_data);
          setShowReview(true);
        }

        setFileList([]);
      }

    } catch (error) {
      console.error('Error submitting data:', error);
      message.error('Failed to process data: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const isFormValid = () => {
    if (inputMethod === 'manual') {
      return formData.company_name !== '';
    } else {
      return fileList.length > 0 && formData.company_name !== '';
    }
  };

  const handleFileUpload = (info) => {
    let newFileList = [...info.fileList].slice(-1); // Keep only the latest file
    setFileList(newFileList);
  };

  const renderLabel = (label, tooltip) => (
    <div className="flex items-center">
      <span>{label}</span>
      <Tooltip title={tooltip}>
        <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
      </Tooltip>
    </div>
  );

  const renderInputField = (key, label) => {
    const value = formData[key];
    const isPercentage = ['participation_rate','deferral_rate','loan_withdrawal_usage','employees_registered_online','employees_opting_for_paper_statements','interaction_rate','employees_build_their_own_portfolio','use_target_or_investment_models'].includes(key);
    
    if (key === 'advisors_note') {
      return (
        <div key={key}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel(label, getTooltipText(key))}
          </label>
          <TextArea
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
            rows={3}
            placeholder={`Enter ${label.toLowerCase()}`}
            className="w-full"
          />
        </div>
      );
    } else {
      return (
        <div key={key}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel(label, getTooltipText(key))}
          </label>
          <Input
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
            style={{ width: '100%' }}
            placeholder={isPercentage ? 'Enter percentage' : 'Enter value'}
            suffix={isPercentage ? '%' : null}
          />
        </div>
      );
    }
  };

  const getTooltipText = (key) => {
    const tooltips = {
      advisors_note: "Add any specific observations or comments about the plan's performance",
      advisor_yearly_compensation: "Total annual compensation received for managing this plan",
      plan_size: "Total assets under management in the plan",
      participation_rate: "Percentage of eligible employees participating in the plan",
      number_of_participants: "Total number of employees enrolled in the plan",
      deferral_rate: "Average percentage of salary being deferred by participants",
      average_account_balance: "Average balance across all participant accounts",
      loan_withdrawal_usage: "Percentage of participants with outstanding loans",
      employees_registered_online: "Percentage of participants registered for online access",
      employees_opting_for_paper_statements: "Percentage receiving paper statements",
      interaction_rate: "Percentage of participants actively managing their accounts",
      number_of_plans_available: "Total number of investment options available",
      employees_build_their_own_portfolio: "Percentage of participants self-directing investments",
      use_target_or_investment_models: "Percentage using target date or model portfolios"
    };
    return tooltips[key] || "Enter value";
  };

  const handlePDFProcess = async () => {
    try {
      if (fileList.length === 0 || !formData.company_name) {
        message.error('Please select both a company and a PDF file');
        return;
      }

      setIsProcessing(true);
      const formDataObj = new FormData();
      formDataObj.append('file', fileList[0].originFileObj);
      formDataObj.append('company_name', formData.company_name);

      const userId = localStorage.getItem('userid');
      const year = dayjs().year();

      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/plan-performance/${year}/process-pdf`, {
        method: 'POST',
        headers: {
          'user-id': userId,
        },
        body: formDataObj
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.extracted_data) {
        // Store the extracted data 
        setExtractedData(data.extracted_data);
        setShowReview(true);
      } else {
        throw new Error('No data extracted from PDF');
      }

    } catch (error) {
      console.error('Error processing PDF:', error);
      message.error('Failed to process PDF: ' + error.message);
      // Reset file list on error
      setFileList([]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveExtractedData = () => {
    // Create a new form data object with all required fields
    const formatExtractedValue = (key, value) => {
      // Handle nested value/page/section structure
      if (value && typeof value === 'object' && 'value' in value) {
        const extractedValue = value.value;
        // Return default value for "No Data Found - Enter Manually"
        if (typeof extractedValue === 'string' && extractedValue === "No Data Found - Enter Manually") {
          return numericFields.includes(key) ? 0 : '';
        }
        return extractedValue;
      }
      
      // Also handle direct values that might be "No Data Found - Enter Manually"
      if (typeof value === 'string' && value === "No Data Found - Enter Manually") {
        return numericFields.includes(key) ? 0 : '';
      }
      
      return value;
    };

    // Use the formatted_data from the backend if available
    const dataSource = extractedData.formatted_data || extractedData;
    
    const newFormData = {
      company_name: formData.company_name,
      advisors_note: formatExtractedValue('advisors_note', dataSource.advisors_note || extractedData["Advisor's Note (Optional)"]) || '',
      advisor_yearly_compensation: formatExtractedValue('advisor_yearly_compensation', dataSource.advisor_yearly_compensation || extractedData["Advisor Yearly Compensation"]) || 0,
      plan_size: formatExtractedValue('plan_size', dataSource.plan_size || extractedData["Plan Size"]) || 0,
      participation_rate: formatExtractedValue('participation_rate', dataSource.participation_rate || extractedData["Participation Rate"]) || 0,
      number_of_participants: formatExtractedValue('number_of_participants', dataSource.number_of_participants || extractedData["Number of Participants"]) || 0,
      deferral_rate: formatExtractedValue('deferral_rate', dataSource.deferral_rate || extractedData["Deferral Rate"]) || 0,
      average_account_balance: formatExtractedValue('average_account_balance', dataSource.average_account_balance || extractedData["Average Account Balance"]) || 0,
      loan_withdrawal_usage: formatExtractedValue('loan_withdrawal_usage', dataSource.loan_withdrawal_usage || extractedData["Loan Withdrawal Usage"]) || 0,
      employees_registered_online: formatExtractedValue('employees_registered_online', dataSource.employees_registered_online || extractedData["Employees Registered Online"]) || 0,
      employees_opting_for_paper_statements: formatExtractedValue('employees_opting_for_paper_statements', dataSource.employees_opting_for_paper_statements || extractedData["Employees Opting For Paper Statements"]) || 0,
      interaction_rate: formatExtractedValue('interaction_rate', dataSource.interaction_rate || extractedData["Interaction Rate"]) || 0,
      number_of_plans_available: formatExtractedValue('number_of_plans_available', dataSource.number_of_plans_available || extractedData["Number of Plans Available"]) || 0,
      employees_build_their_own_portfolio: formatExtractedValue('employees_build_their_own_portfolio', dataSource.employees_build_their_own_portfolio || extractedData["Employees Build Their Own Portfolio"]) || 0,
      use_target_or_investment_models: formatExtractedValue('use_target_or_investment_models', dataSource.use_target_or_investment_models || extractedData["Use Target or Investment Models"]) || 0
    };

    // Log the extracted data and new form data for debugging
    console.log('Extracted data:', extractedData);
    console.log('New form data:', newFormData);

    // Submit the form with the new data
    const userId = localStorage.getItem('userid');
    const year = dayjs().year();

    fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/plan-performance/${year}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'user-id': userId,
        'accept': 'application/json'
      },
      body: JSON.stringify(newFormData)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(() => {
      setShowReview(false);
      setExtractedData(null);
      setFileList([]);
      message.success('Data saved successfully with your edits');
      if (onDataSubmitted) {
        onDataSubmitted();
      }
    })
    .catch(error => {
      console.error('Error saving data:', error);
      message.error('Failed to save data: ' + error.message);
    });
  };

  const renderExtractedDataReview = () => {
    if (!extractedData) return null;

    console.log('Current extracted data:', extractedData); // Add logging to debug

    // Helper function to extract key-value pairs from possibly nested structure
    const getFieldsForReview = () => {
      const result = [];
      
      // Get all keys except company_name, message, save_result
      Object.entries(extractedData)
        .filter(([key]) => key !== 'company_name' && key !== 'message' && key !== 'save_result')
        .forEach(([key, value]) => {
          // Translate backend field names to frontend field names if needed
          const displayKey = key.replace(/[()-\s]/g, '_').toLowerCase();
          const displayName = displayNameMap[displayKey] || key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
          
          // Extract value from value/page/section structure if present
          let extractedValue = value;
          if (value && typeof value === 'object' && 'value' in value) {
            extractedValue = value.value;
            
            // Add page/section info to field info for additional context
            if (value.page || value.section) {
              const locationInfo = [];
              if (value.page) locationInfo.push(`Page ${value.page}`);
              if (value.section) locationInfo.push(`Section: ${value.section}`);
              
              result.push({
                key: `${displayKey}_location`,
                field: `${displayName} (Location)`,
                value: locationInfo.join(', '),
                editable: false,
                isInfo: true
              });
            }
          }
          
          result.push({
            key: displayKey,
            field: displayName,
            value: extractedValue || '',
            editable: true,
            isInfo: false
          });
        });
      
      return result;
    };

    const dataSource = getFieldsForReview();
    console.log('Table data source:', dataSource); // Add logging to debug

    const columns = [
      {
        title: 'Field',
        dataIndex: 'field',
        key: 'field',
        width: '30%',
        render: (text, record) => {
          return <span className={`font-medium ${record.isInfo ? 'text-gray-500 italic text-sm' : 'text-gray-700'}`}>{text}</span>;
        }
      },
      {
        title: 'Extracted Value',
        dataIndex: 'value',
        key: 'value',
        width: '70%',
        render: (text, record) => {
          if (record.isInfo) {
            return <div className="text-gray-500 italic text-sm">{text}</div>;
          }
          
          const originalKey = record.key; // Use the original snake_case key
          const isPercentage = [
            'participation_rate',
            'deferral_rate',
            'loan_withdrawal_usage',
            'employees_registered_online',
            'employees_opting_for_paper_statements',
            'interaction_rate',
            'employees_build_their_own_portfolio',
            'use_target_or_investment_models'
          ].includes(originalKey);

          // For uneditable information fields
          if (!record.editable) {
            return <div>{text}</div>;
          }
          
          // Special handling for "No Data Found - Enter Manually" values
          if (text === "No Data Found - Enter Manually") {
            return (
              <Input
                defaultValue=""
                placeholder="No data found - please enter manually"
                suffix={isPercentage ? '%' : null}
                onChange={(e) => {
                  setExtractedData(prev => ({
                    ...prev,
                    [originalKey]: e.target.value
                  }));
                }}
                className="bg-yellow-50 border-yellow-300"
              />
            );
          }

          return (
            <Input
              value={text}
              suffix={isPercentage ? '%' : null}
              onChange={(e) => {
                setExtractedData(prev => {
                  // Handle nested value/page/section structure if it exists
                  const updatedValue = {...prev};
                  if (prev[originalKey] && typeof prev[originalKey] === 'object' && 'value' in prev[originalKey]) {
                    updatedValue[originalKey] = {
                      ...prev[originalKey],
                      value: e.target.value
                    };
                  } else {
                    updatedValue[originalKey] = e.target.value;
                  }
                  return updatedValue;
                });
              }}
            />
          );
        },
      }
    ];

    return (
      <div className="mt-8">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
          <p className="text-amber-800">
            <strong>Review and Edit:</strong> The information below was extracted from your PDF. 
            Please review and edit any fields as needed before saving.
          </p>
          <p className="text-amber-700 mt-2">
            Company Name: <strong>{formData.company_name}</strong>
          </p>
        </div>
        
        <Title level={4}>Review Extracted Data</Title>
        <Table 
          dataSource={dataSource} 
          columns={columns} 
          pagination={false}
          className="mb-4"
          rowClassName="align-top"
        />
        
        <div className="flex justify-end space-x-4 mt-4">
          <Button onClick={() => {
            setShowReview(false);
            setExtractedData(null);
            setFileList([]);
          }}>
            Cancel
          </Button>
          <Button 
            type="primary"
            onClick={handleSaveExtractedData}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Save Edited Data
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-8 bg-white rounded-xl">
      <h1 className="text-3xl font-light text-gray-800 mb-6">Plan Performance Data Entry</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-1 gap-8 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {renderLabel("Company Name", "Select the company whose plan performance you're recording.")}
          </label>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Select a company"
            value={formData.company_name}
            onChange={(value) => handleChange('company_name', value)}
            className="text-lg"
            filterOption={(input, option) => 
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {companyOptions.map(option => (
              <Option key={option.value} value={option.value}>{option.label}</Option>
            ))}
          </Select>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {renderLabel("Input Method", "Choose between manual data entry or uploading a PDF report.")}
          </label>
          <Radio.Group 
            onChange={(e) => setInputMethod(e.target.value)} 
            value={inputMethod}
            className="flex space-x-4"
          >
            <Radio value="manual" className="text-lg">Manual Entry</Radio>
            <Radio value="pdf" className="text-lg">Upload PDF</Radio>
          </Radio.Group>
        </div>
      </div>

      {inputMethod === 'manual' && !showReview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {renderInputField('advisors_note', "Advisor's Note (Optional)")}
          {renderInputField('advisor_yearly_compensation', 'Advisor Yearly Compensation')}
          {renderInputField('plan_size', 'Plan Size')}
          {renderInputField('participation_rate', 'Participation Rate')}
          {renderInputField('number_of_participants', 'Number of Participants')}
          {renderInputField('deferral_rate', 'Deferral Rate')}
          {renderInputField('average_account_balance', 'Average Account Balance')}
          {renderInputField('loan_withdrawal_usage', 'Loan Withdrawal Usage')}
          {renderInputField('employees_registered_online', 'Employees Registered Online')}
          {renderInputField('employees_opting_for_paper_statements', 'Employees Opting For Paper Statements')}
          {renderInputField('interaction_rate', 'Interaction Rate')}
          {renderInputField('number_of_plans_available', 'Number of Plans Available')}
          {renderInputField('employees_build_their_own_portfolio', 'Employees Build Their Own Portfolio')}
          {renderInputField('use_target_or_investment_models', 'Use Target Or Investment Models')}
        </div>
      )}

      {inputMethod === 'pdf' && !showReview && (
        <div className="bg-gray-50 p-6 rounded-lg border-2 border-dashed border-gray-300">
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-blue-800">
              <strong>Note:</strong> For best results, please upload a "Plan Health Report" PDF. Processing may take up to a minute.
            </p>
          </div>
          <Tooltip title="Upload a PDF report containing plan performance data. The system will automatically extract the relevant information.">
            <Upload.Dragger
              name="file"
              multiple={false}
              fileList={fileList}
              onChange={handleFileUpload}
              accept=".pdf"
              beforeUpload={() => false}
              className="bg-white"
            >
              <p className="ant-upload-drag-icon">
                <UploadIcon className="h-16 w-16 text-blue-500 mx-auto" />
              </p>
              <p className="ant-upload-text text-lg font-medium mt-4">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint text-sm text-gray-500">
                Support for a single PDF file. The file will be processed to extract plan performance information.
              </p>
            </Upload.Dragger>
          </Tooltip>
        </div>
      )}

      {showReview && renderExtractedDataReview()}

      {!showReview && (
        <div className="mt-8 flex justify-end">
          <Tooltip title="Save this plan performance data to the database">
            <Button 
              onClick={inputMethod === 'manual' ? handleSubmit : handlePDFProcess}
              disabled={!isFormValid() || isProcessing}
              size="large"
              className={`inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-md shadow-sm text-white ${
                isFormValid() && !isProcessing 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-gray-300 cursor-not-allowed'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out`}
            >
              {isProcessing ? (
                <Spin className="mr-2" />
              ) : (
                <FileText className="h-5 w-5 mr-2" />
              )}
              {inputMethod === 'manual' ? 'Submit Plan Performance' : (isProcessing ? 'Processing PDF...' : 'Process PDF')}
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default AutomaticDataEntryForPlanPerformance;